/**
 * Demo family (section 20.6). Five generations of the Edry family with
 * living and deceased people, English-only / Hebrew-only / bilingual names,
 * duplicate names, current and former partners, adoptive / step / unspecified
 * parent links, people without accounts, a temporary-access elder, a child
 * managed by a guardian, and pending review items.
 */
import type {
  AccessCode, Account, Family, FamilyMembership, FuzzyDate, Gender, Invitation, LivingStatus, Location, Person,
  PersonAccountLink, PersonName, ProfileManagementGrant, Relationship, TemporaryAccessSession, UUID,
} from '../../../domain/types';
import { DEFAULT_GOVERNANCE } from '../../../domain/types';
import { seedId } from '../../../lib/uuid';
import { daysAgoISO, daysFromNowISO } from '../../../lib/time';

export const P = (k: string): UUID => seedId('person', k);
export const A = (k: string): UUID => seedId('account', k);
export const L = (k: string): UUID => seedId('location', k);
export const FAMILY_ID = seedId('family', 'edry');

export const DEMO_ACCESS_CODE = 'EDRY-2026';
export const DEMO_TEMP_CODE = 'MIRIAM-7Q2K';
export const DEMO_INVITE_TOKEN = 'INV-NATHALIE-2026';
export const DEMO_PASSWORD = 'family';

const created = daysAgoISO(400);

interface NameSpec {
  given: string;
  family: string;
  nickname?: string;
  middle?: string;
  display?: string;
  previous?: string[];
}

interface PersonSpec {
  key: string;
  en?: NameSpec;
  he?: NameSpec;
  gender: Gender;
  status: LivingStatus;
  birth: FuzzyDate;
  death?: FuzzyDate;
  hue: number;
  birthPlace?: string;
  notes?: string;
  reviewStatus?: Person['reviewStatus'];
  lockedFields?: string[];
  lockReason?: string;
}

const ex = (d: string): FuzzyDate => ({ kind: 'exact', start: d, certainty: 'confirmed' });
const yr = (y: number): FuzzyDate => ({ kind: 'year', start: String(y), certainty: 'confirmed' });
const about = (y: number): FuzzyDate => ({ kind: 'about', start: String(y), certainty: 'approximate' });
const decade = (y: number): FuzzyDate => ({ kind: 'decade', start: String(y), end: String(y + 9), certainty: 'approximate' });
const month = (ym: string): FuzzyDate => ({ kind: 'month', start: ym, certainty: 'confirmed' });
const unknown = (): FuzzyDate => ({ kind: 'unknown' });

export const PEOPLE: PersonSpec[] = [
  // ---- Generation 0 (oldest) ---------------------------------------------
  { key: 'yaakov', he: { given: 'יעקב', family: 'אדרי' }, gender: 'male', status: 'deceased', birth: about(1898), death: yr(1971), hue: 0, birthPlace: 'casablanca', lockedFields: ['names', 'birth', 'death'], lockReason: 'Verified against the family record book (locked by Sarah).' },
  { key: 'rachel', en: { given: 'Rachel', family: 'Edry', previous: ['Rachel Ohana'] }, he: { given: 'רחל', family: 'אדרי', previous: ['רחל אוחנה'] }, gender: 'female', status: 'deceased', birth: decade(1900), death: yr(1980), hue: 1, birthPlace: 'casablanca' },
  // ---- Generation 1 -------------------------------------------------------
  { key: 'moshe', en: { given: 'Moshe', family: 'Edry', nickname: 'Saba Moshe' }, he: { given: 'משה', family: 'אדרי', nickname: 'סבא משה' }, gender: 'male', status: 'deceased', birth: yr(1925), death: ex('2003-11-09'), hue: 2, birthPlace: 'casablanca' },
  { key: 'miriam', en: { given: 'Miriam', family: 'Edry', nickname: 'Savta Miriam', previous: ['Miriam Abitbol'] }, he: { given: 'מרים', family: 'אדרי', nickname: 'סבתא מרים', previous: ['מרים אביטבול'] }, gender: 'female', status: 'living', birth: ex('1932-04-18'), hue: 3, birthPlace: 'fes', notes: 'Uses the tablet in the living room. Prefers Hebrew.' },
  { key: 'shimon', en: { given: 'Shimon', family: 'Edry' }, he: { given: 'שמעון', family: 'אדרי' }, gender: 'male', status: 'deceased', birth: yr(1928), death: yr(1999), hue: 4, birthPlace: 'casablanca' },
  { key: 'esther', en: { given: 'Esther', family: 'Edry' }, he: { given: 'אסתר', family: 'אדרי' }, gender: 'female', status: 'deceased', birth: about(1930), death: yr(2015), hue: 5 },
  { key: 'yosef', he: { given: 'יוסף', family: 'אדרי', nickname: 'דוד יוסי' }, gender: 'male', status: 'living', birth: yr(1935), hue: 6, birthPlace: 'casablanca', notes: 'No account. Stories uploaded by David.' },
  // ---- Generation 2 -------------------------------------------------------
  { key: 'david', en: { given: 'David', family: 'Edry' }, he: { given: 'דוד', family: 'אדרי' }, gender: 'male', status: 'living', birth: ex('1955-02-11'), hue: 7, birthPlace: 'haifa' },
  { key: 'ruth', en: { given: 'Ruth', family: 'Edry', previous: ['Ruth Levi'] }, he: { given: 'רות', family: 'אדרי', previous: ['רות לוי'] }, gender: 'female', status: 'living', birth: yr(1958), hue: 8, birthPlace: 'telaviv' },
  { key: 'dalia', en: { given: 'Dalia', family: 'Cohen' }, he: { given: 'דליה', family: 'כהן' }, gender: 'female', status: 'living', birth: yr(1956), hue: 9 },
  { key: 'leah', en: { given: 'Leah', family: 'Mizrahi', previous: ['Leah Edry'] }, he: { given: 'לאה', family: 'מזרחי', previous: ['לאה אדרי'] }, gender: 'female', status: 'living', birth: ex('1960-07-30'), hue: 10, birthPlace: 'haifa' },
  { key: 'avi', en: { given: 'Avi', family: 'Mizrahi' }, he: { given: 'אבי', family: 'מזרחי' }, gender: 'male', status: 'living', birth: yr(1957), hue: 11 },
  { key: 'david2', en: { given: 'David', family: 'Edry' }, he: { given: 'דוד', family: 'אדרי' }, gender: 'male', status: 'living', birth: yr(1953), hue: 12, birthPlace: 'haifa', notes: 'Shimon’s son — same name as his cousin David.' },
  { key: 'nathalie', en: { given: 'Nathalie', family: 'Edry-Dubois' }, gender: 'female', status: 'living', birth: yr(1962), hue: 13, birthPlace: 'montreal', notes: 'Lives in Montreal. Invitation sent.' },
  // ---- Generation 3 -------------------------------------------------------
  { key: 'sarah', en: { given: 'Sarah', family: 'Edry-Katz', previous: ['Sarah Edry'] }, he: { given: 'שרה', family: 'אדרי-כץ', previous: ['שרה אדרי'] }, gender: 'female', status: 'living', birth: ex('1984-05-03'), hue: 14, birthPlace: 'netanya' },
  { key: 'tom', en: { given: 'Tom', family: 'Katz' }, he: { given: 'תום', family: 'כץ' }, gender: 'male', status: 'living', birth: ex('1982-12-21'), hue: 15, birthPlace: 'telaviv' },
  { key: 'gideon', en: { given: 'Gideon', family: 'Katz' }, he: { given: 'גדעון', family: 'כץ' }, gender: 'male', status: 'living', birth: yr(1950), hue: 0, notes: 'Profile managed by his son Tom.' },
  { key: 'eitan', en: { given: 'Eitan', family: 'Edry' }, he: { given: 'איתן', family: 'אדרי' }, gender: 'male', status: 'living', birth: ex('1978-03-14'), hue: 1, birthPlace: 'haifa' },
  { key: 'noa', en: { given: 'Noa', family: 'Edry' }, he: { given: 'נועה', family: 'אדרי' }, gender: 'female', status: 'living', birth: ex('1988-09-02'), hue: 2, birthPlace: 'netanya' },
  { key: 'maya', en: { given: 'Maya', family: 'Levi' }, he: { given: 'מאיה', family: 'לוי' }, gender: 'female', status: 'living', birth: yr(1989), hue: 3 },
  { key: 'yonatan', en: { given: 'Yonatan', family: 'Mizrahi', nickname: 'Yoni' }, he: { given: 'יונתן', family: 'מזרחי', nickname: 'יוני' }, gender: 'male', status: 'living', birth: yr(1986), hue: 4 },
  { key: 'daniel', en: { given: 'Daniel', family: 'Edry' }, he: { given: 'דניאל', family: 'אדרי' }, gender: 'male', status: 'living', birth: yr(1980), hue: 5, birthPlace: 'haifa' },
  // ---- Generation 4 -------------------------------------------------------
  { key: 'lior', en: { given: 'Lior', family: 'Katz' }, he: { given: 'ליאור', family: 'כץ' }, gender: 'male', status: 'living', birth: ex('2012-06-15'), hue: 6, notes: 'Minor. Guardian: Sarah.' },
  { key: 'tamar', en: { given: 'Tamar', family: 'Katz' }, he: { given: 'תמר', family: 'כץ' }, gender: 'female', status: 'living', birth: ex('2016-01-27'), hue: 7, notes: 'Minor. Guardian: Sarah.' },
  { key: 'ori', en: { given: 'Ori', family: 'Edry' }, he: { given: 'אורי', family: 'אדרי' }, gender: 'other', status: 'living', birth: ex('2015-10-08'), hue: 8 },
  { key: 'ella', en: { given: 'Ella', family: 'Mizrahi' }, he: { given: 'אלה', family: 'מזרחי' }, gender: 'female', status: 'living', birth: yr(2014), hue: 9 },
  // ---- Probable duplicate awaiting review ---------------------------------
  { key: 'david_dup', en: { given: 'David', family: 'Edri' }, gender: 'male', status: 'living', birth: about(1955), hue: 10, reviewStatus: 'pending', notes: 'Added by a new member through the family code — may be the same person as David Edry (b. 1955).' },
];

interface RelSpec {
  type: 'parent_child' | 'partner';
  from: string;
  to: string;
  subtype?: Relationship['subtype'];
  current?: boolean;
  start?: FuzzyDate;
  end?: FuzzyDate;
  status?: Relationship['status'];
  note?: string;
  locked?: boolean;
}

export const RELATIONSHIPS: RelSpec[] = [
  { type: 'partner', from: 'yaakov', to: 'rachel', subtype: 'married', current: true, start: decade(1920), locked: true },
  { type: 'parent_child', from: 'yaakov', to: 'moshe', subtype: 'biological', locked: true },
  { type: 'parent_child', from: 'rachel', to: 'moshe', subtype: 'biological', locked: true },
  { type: 'parent_child', from: 'yaakov', to: 'shimon', subtype: 'biological' },
  { type: 'parent_child', from: 'rachel', to: 'shimon', subtype: 'biological' },
  { type: 'parent_child', from: 'yaakov', to: 'yosef', subtype: 'unspecified' },
  { type: 'parent_child', from: 'rachel', to: 'yosef', subtype: 'unspecified' },

  { type: 'partner', from: 'moshe', to: 'miriam', subtype: 'married', current: true, start: month('1951-06') },
  { type: 'parent_child', from: 'moshe', to: 'david', subtype: 'biological' },
  { type: 'parent_child', from: 'miriam', to: 'david', subtype: 'biological' },
  { type: 'parent_child', from: 'moshe', to: 'leah', subtype: 'biological' },
  { type: 'parent_child', from: 'miriam', to: 'leah', subtype: 'biological' },

  { type: 'partner', from: 'shimon', to: 'esther', subtype: 'married', current: true, start: about(1952) },
  { type: 'parent_child', from: 'shimon', to: 'david2', subtype: 'biological' },
  { type: 'parent_child', from: 'esther', to: 'david2', subtype: 'biological' },
  { type: 'parent_child', from: 'shimon', to: 'nathalie', subtype: 'biological' },
  { type: 'parent_child', from: 'esther', to: 'nathalie', subtype: 'biological' },

  // David's former and current partners
  { type: 'partner', from: 'david', to: 'dalia', subtype: 'married', current: false, start: yr(1976), end: yr(1980) },
  { type: 'partner', from: 'david', to: 'ruth', subtype: 'married', current: true, start: ex('1982-08-15') },
  { type: 'parent_child', from: 'david', to: 'eitan', subtype: 'biological' },
  // Pending correction: Eitan asked to add Dalia as his mother (section 10.5)
  { type: 'parent_child', from: 'dalia', to: 'eitan', subtype: 'biological', status: 'pending', note: 'Requested by Eitan' },
  { type: 'parent_child', from: 'ruth', to: 'eitan', subtype: 'step' },
  { type: 'parent_child', from: 'david', to: 'sarah', subtype: 'biological' },
  { type: 'parent_child', from: 'ruth', to: 'sarah', subtype: 'biological' },
  { type: 'parent_child', from: 'david', to: 'noa', subtype: 'adoptive' },
  { type: 'parent_child', from: 'ruth', to: 'noa', subtype: 'adoptive' },

  { type: 'partner', from: 'leah', to: 'avi', subtype: 'married', current: true, start: yr(1984) },
  { type: 'parent_child', from: 'leah', to: 'yonatan', subtype: 'biological' },
  { type: 'parent_child', from: 'avi', to: 'yonatan', subtype: 'biological' },

  { type: 'parent_child', from: 'david2', to: 'daniel', subtype: 'biological' },

  { type: 'partner', from: 'sarah', to: 'tom', subtype: 'married', current: true, start: ex('2010-09-12') },
  { type: 'parent_child', from: 'gideon', to: 'tom', subtype: 'biological' },
  { type: 'parent_child', from: 'sarah', to: 'lior', subtype: 'biological' },
  { type: 'parent_child', from: 'tom', to: 'lior', subtype: 'biological' },
  { type: 'parent_child', from: 'sarah', to: 'tamar', subtype: 'biological' },
  { type: 'parent_child', from: 'tom', to: 'tamar', subtype: 'biological' },

  { type: 'partner', from: 'noa', to: 'maya', subtype: 'partnered', current: true, start: yr(2013) },
  { type: 'parent_child', from: 'noa', to: 'ori', subtype: 'biological' },
  { type: 'parent_child', from: 'maya', to: 'ori', subtype: 'unspecified' },

  { type: 'parent_child', from: 'yonatan', to: 'ella', subtype: 'biological' },
];

export const LOCATIONS: Array<{ key: string; label: string; labelHe: string; historical?: string }> = [
  { key: 'casablanca', label: 'Casablanca, Morocco', labelHe: 'קזבלנקה, מרוקו' },
  { key: 'fes', label: 'Fes, Morocco', labelHe: 'פאס, מרוקו' },
  { key: 'haifa', label: 'Haifa', labelHe: 'חיפה' },
  { key: 'telaviv', label: 'Tel Aviv', labelHe: 'תל אביב' },
  { key: 'netanya', label: 'Netanya', labelHe: 'נתניה' },
  { key: 'montreal', label: 'Montreal, Canada', labelHe: 'מונטריאול, קנדה' },
  { key: 'herzl', label: 'Herzl Street, Haifa', labelHe: 'רחוב הרצל, חיפה' },
];

interface AccountSpec {
  key: string;
  kind: Account['kind'];
  email?: string;
  phone?: string;
  displayName: string;
  displayNameHe: string;
  lang: 'en' | 'he';
  person: string | null;
  role: FamilyMembership['role'];
  hint: string;
  hintHe: string;
}

export const ACCOUNTS: AccountSpec[] = [
  { key: 'sarah', kind: 'email', email: 'sarah@edry.example', displayName: 'Sarah Edry-Katz', displayNameHe: 'שרה אדרי-כץ', lang: 'en', person: 'sarah', role: 'admin', hint: 'Administrator · helps Savta Miriam · guardian of Lior & Tamar', hintHe: 'מנהלת · עוזרת לסבתא מרים · אפוטרופוסית של ליאור ותמר' },
  { key: 'david', kind: 'email', email: 'david@edry.example', displayName: 'David Edry', displayNameHe: 'דוד אדרי', lang: 'he', person: 'david', role: 'editor', hint: 'Family Editor · reviews changes and duplicates', hintHe: 'עורך משפחה · בודק שינויים וכפילויות' },
  { key: 'tom', kind: 'email', email: 'tom@edry.example', displayName: 'Tom Katz', displayNameHe: 'תום כץ', lang: 'en', person: 'tom', role: 'member', hint: 'Member · Profile Helper for his father Gideon', hintHe: 'חבר · עוזר פרופיל לאביו גדעון' },
  { key: 'noa', kind: 'phone', phone: '+972-52-000-0004', displayName: 'Noa Edry', displayNameHe: 'נועה אדרי', lang: 'he', person: 'noa', role: 'member', hint: 'Member · signed in with a phone code (no email)', hintHe: 'חברה · התחברה עם קוד בטלפון (ללא אימייל)' },
  { key: 'eitan', kind: 'email', email: 'eitan@edry.example', displayName: 'Eitan Edry', displayNameHe: 'איתן אדרי', lang: 'en', person: 'eitan', role: 'member', hint: 'Member · has a correction awaiting review', hintHe: 'חבר · תיקון שלו ממתין לבדיקה' },
  { key: 'daniel', kind: 'email', email: 'daniel@edry.example', displayName: 'Daniel Edry', displayNameHe: 'דניאל אדרי', lang: 'en', person: 'daniel', role: 'admin', hint: 'Administrator · the second vote on family decisions', hintHe: 'מנהל · הקול השני בהחלטות המשפחה' },
  { key: 'miriam', kind: 'temporary', displayName: 'Miriam Edry', displayNameHe: 'מרים אדרי', lang: 'he', person: 'miriam', role: 'member', hint: 'Temporary trusted-device access on her tablet · unlocks with a PIN', hintHe: 'גישה זמנית ממכשיר מהימן בטאבלט · נפתח עם קוד PIN' },
  { key: 'yonatan', kind: 'email', email: 'yoni.m@example.com', displayName: 'Yonatan Mizrahi', displayNameHe: 'יונתן מזרחי', lang: 'he', person: null, role: 'member', hint: 'Joined with the family code · claim of "Yonatan Mizrahi" awaiting approval', hintHe: 'הצטרף עם קוד המשפחה · בקשת שיוך ל"יונתן מזרחי" ממתינה לאישור' },
];

export function buildPeople(): { persons: Person[]; names: PersonName[] } {
  const persons: Person[] = [];
  const names: PersonName[] = [];
  for (const s of PEOPLE) {
    const id = P(s.key);
    persons.push({
      id,
      familyId: FAMILY_ID,
      status: s.status,
      gender: s.gender,
      birth: s.birth,
      death: s.death,
      birthPlaceId: s.birthPlace ? L(s.birthPlace) : undefined,
      portraitHue: s.hue,
      reviewStatus: s.reviewStatus ?? 'approved',
      lockedFields: s.lockedFields ?? [],
      lockReason: s.lockReason,
      archived: false,
      notes: s.notes,
      version: 1,
      createdAt: created,
      updatedAt: created,
      updatedBy: A('sarah'),
    });
    if (s.en) {
      names.push({ id: seedId('name', `${s.key}:en`), personId: id, language: 'en', given: s.en.given, family: s.en.family, nickname: s.en.nickname, middle: s.en.middle, display: s.en.display, preferred: true, previousNames: s.en.previous });
    }
    if (s.he) {
      names.push({ id: seedId('name', `${s.key}:he`), personId: id, language: 'he', given: s.he.given, family: s.he.family, nickname: s.he.nickname, middle: s.he.middle, display: s.he.display, preferred: true, previousNames: s.he.previous });
    }
  }
  return { persons, names };
}

export function buildRelationships(): Relationship[] {
  return RELATIONSHIPS.map((r, i) => {
    const from = P(r.from);
    const to = P(r.to);
    const canonicalFrom = r.type === 'partner' && to < from ? to : from;
    const canonicalTo = r.type === 'partner' && to < from ? from : to;
    return {
      id: seedId('rel', `${i}:${r.from}:${r.to}`),
      familyId: FAMILY_ID,
      type: r.type,
      fromPersonId: canonicalFrom,
      toPersonId: canonicalTo,
      subtype: r.subtype ?? 'unspecified',
      current: r.current,
      startDate: r.start,
      endDate: r.end,
      status: r.status ?? 'approved',
      note: r.note,
      locked: r.locked ?? false,
      version: 1,
      createdAt: created,
      updatedAt: created,
      updatedBy: A('sarah'),
    };
  });
}

export function buildLocations(): Location[] {
  return LOCATIONS.map((l) => ({ id: L(l.key), label: l.label, labelHe: l.labelHe, historicalName: l.historical }));
}

export function buildFamily(): Family {
  return {
    id: FAMILY_ID,
    name: 'The Edry Family',
    nameHe: 'משפחת אדרי',
    defaultLang: 'en',
    settings: { memberRelationshipChanges: 'review', allowUnknownBirthDate: true },
    // Administrators are named by email. Two of them, so "a majority of the
    // administrators" is a real threshold in the demo rather than a formality.
    governance: { ...DEFAULT_GOVERNANCE, adminEmails: ['sarah@edry.example', 'daniel@edry.example'] },
    createdAt: created,
  };
}

export function buildAccounts(): { accounts: Account[]; memberships: FamilyMembership[]; links: PersonAccountLink[]; grants: ProfileManagementGrant[] } {
  const accounts: Account[] = [];
  const memberships: FamilyMembership[] = [];
  const links: PersonAccountLink[] = [];
  for (const a of ACCOUNTS) {
    accounts.push({
      id: A(a.key),
      kind: a.kind,
      email: a.email,
      phone: a.phone,
      displayName: a.displayName,
      preferredLang: a.lang,
      createdAt: created,
      hasRecoveryIdentity: a.kind !== 'temporary',
    });
    memberships.push({ id: seedId('membership', a.key), familyId: FAMILY_ID, accountId: A(a.key), role: a.role, status: 'active', createdAt: created });
    if (a.person) links.push({ id: seedId('link', a.key), accountId: A(a.key), personId: P(a.person), kind: 'self', status: 'active', createdAt: created });
  }
  // Pending claim from Yonatan's new account
  links.push({ id: seedId('link', 'yonatan-pending'), accountId: A('yonatan'), personId: P('yonatan'), kind: 'self', status: 'pending', createdAt: daysAgoISO(2) });

  const grants: ProfileManagementGrant[] = [
    { id: seedId('grant', 'sarah-miriam'), familyId: FAMILY_ID, helperAccountId: A('sarah'), managedPersonId: P('miriam'), scope: 'full', grantedByAccountId: A('sarah'), status: 'active', createdAt: created },
    { id: seedId('grant', 'sarah-lior'), familyId: FAMILY_ID, helperAccountId: A('sarah'), managedPersonId: P('lior'), scope: 'full', grantedByAccountId: A('sarah'), status: 'active', createdAt: created },
    { id: seedId('grant', 'sarah-tamar'), familyId: FAMILY_ID, helperAccountId: A('sarah'), managedPersonId: P('tamar'), scope: 'full', grantedByAccountId: A('sarah'), status: 'active', createdAt: created },
    { id: seedId('grant', 'tom-gideon'), familyId: FAMILY_ID, helperAccountId: A('tom'), managedPersonId: P('gideon'), scope: 'full', grantedByAccountId: A('sarah'), status: 'active', createdAt: daysAgoISO(30) },
    { id: seedId('grant', 'david-yosef'), familyId: FAMILY_ID, helperAccountId: A('david'), managedPersonId: P('yosef'), scope: 'record_only', grantedByAccountId: A('sarah'), status: 'active', createdAt: daysAgoISO(200) },
    { id: seedId('grant', 'noa-ori'), familyId: FAMILY_ID, helperAccountId: A('noa'), managedPersonId: P('ori'), scope: 'full', grantedByAccountId: A('sarah'), status: 'active', createdAt: daysAgoISO(100) },
  ];
  return { accounts, memberships, links, grants };
}

export function buildAccess(): { codes: AccessCode[]; invitations: Invitation[]; temp: TemporaryAccessSession[] } {
  const codes: AccessCode[] = [
    { id: seedId('code', '2026'), familyId: FAMILY_ID, code: DEMO_ACCESS_CODE, label: 'Family reunion 2026', expiresAt: daysFromNowISO(30), revoked: false, maxUses: 50, uses: 7, createdAt: daysAgoISO(10) },
    { id: seedId('code', '2025'), familyId: FAMILY_ID, code: 'EDRY-2025', label: 'Passover 2025', expiresAt: daysAgoISO(200), revoked: true, maxUses: 50, uses: 12, createdAt: daysAgoISO(300) },
  ];
  const invitations: Invitation[] = [
    { id: seedId('inv', 'nathalie'), familyId: FAMILY_ID, token: DEMO_INVITE_TOKEN, intendedPersonId: P('nathalie'), destination: 'nathalie@example.com', expiresAt: daysFromNowISO(14), status: 'pending', createdByAccountId: A('david'), createdAt: daysAgoISO(1) },
    { id: seedId('inv', 'yonatan'), familyId: FAMILY_ID, token: 'INV-YONATAN-USED', intendedPersonId: P('yonatan'), destination: 'yoni.m@example.com', expiresAt: daysAgoISO(5), status: 'expired', createdByAccountId: A('sarah'), createdAt: daysAgoISO(20) },
    { id: seedId('inv', 'daniel'), familyId: FAMILY_ID, token: 'INV-DANIEL-DONE', intendedPersonId: P('daniel'), destination: 'daniel@edry.example', expiresAt: daysAgoISO(100), status: 'accepted', createdByAccountId: A('sarah'), createdAt: daysAgoISO(120) },
  ];
  const temp: TemporaryAccessSession[] = [
    { id: seedId('temp', 'miriam-tablet'), familyId: FAMILY_ID, personId: P('miriam'), accountId: A('miriam'), code: DEMO_TEMP_CODE, deviceLabel: 'Living-room tablet', issuedByAccountId: A('sarah'), issuedAt: daysAgoISO(3), expiresAt: daysFromNowISO(180), revoked: false },
    { id: seedId('temp', 'miriam-oldphone'), familyId: FAMILY_ID, personId: P('miriam'), accountId: A('miriam'), code: 'MIRIAM-OLD1', deviceLabel: 'Old phone (lost)', issuedByAccountId: A('sarah'), issuedAt: daysAgoISO(90), expiresAt: daysFromNowISO(90), revoked: true, redeemedAt: daysAgoISO(89) },
  ];
  return { codes, invitations, temp };
}
